import React, { useEffect, useRef, useState } from "react";
import { X, MapPin } from "lucide-react";
import axios from "axios";
import { Autocomplete } from "@react-google-maps/api";

const NewAddressModal = ({ isOpen, onClose, onSave, patientId }) => {
  const [formData, setFormData] = useState({
    saveas: "Home",
    address: "",
    landmark: "",
    city: "",
    state: "",
    pincode: "",
    latitude: null,
    longitude: null,
  });

  const [loading, setLoading] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  const inputRef = useRef(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        saveas: "Home",
        address: "",
        landmark: "",
        city: "",
        state: "",
        pincode: "",
        latitude: null,
        longitude: null,
      });
      setErrors({});
      setLoading(false);
      setAutocomplete(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const setField = (key, value) => {
    setFormData((p) => ({ ...p, [key]: value }));
    if (errors[key]) setErrors((p) => ({ ...p, [key]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!formData.address?.trim()) e.address = "Address is required";
    if (!formData.city?.trim()) e.city = "City is required";
    if (!formData.state?.trim()) e.state = "State is required";
    if (!/^\d{6}$/.test(formData.pincode || ""))
      e.pincode = "Valid 6-digit pincode required";
    return e;
  };

  const onLoadAuto = (instance) => setAutocomplete(instance);

  const extractFromPlace = (place) => {
    let city = "";
    let state = "";
    let pincode = "";

    const comps = place?.address_components || [];

    comps.forEach((c) => {
      const t = c.types || [];
      if (t.includes("locality")) city = c.long_name;
      if (!city && t.includes("administrative_area_level_2"))
        city = c.long_name;
      if (t.includes("administrative_area_level_1")) state = c.long_name;
      if (t.includes("postal_code")) pincode = c.long_name;
      // sometimes google returns suffix separately
      if (!pincode && t.includes("postal_code_suffix")) pincode = c.long_name;
    });

    return { city, state, pincode };
  };

  // ✅ fallback: get postal_code using lat/lng
  const reverseGeocodeByLatLng = async (lat, lng) => {
    try {
      if (!window.google?.maps?.Geocoder) return null;

      const geocoder = new window.google.maps.Geocoder();
      const result = await new Promise((resolve, reject) => {
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === "OK" && results?.[0]) resolve(results[0]);
          else reject(new Error(status || "Reverse geocoding failed"));
        });
      });

      const { city, state, pincode } = extractFromPlace(result);
      return { city, state, pincode };
    } catch (err) {
      console.error("reverseGeocodeByLatLng error:", err);
      return null;
    }
  };

  const onPlaceChanged = async () => {
    try {
      if (!autocomplete) return;

      const place = autocomplete.getPlace();
      if (!place?.geometry?.location) return;

      const formatted = place.formatted_address || "";
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      let { city, state, pincode } = extractFromPlace(place);

      // ✅ if postal_code missing, try reverse geocode using lat/lng
      if (!pincode) {
        const fallback = await reverseGeocodeByLatLng(lat, lng);
        if (fallback?.pincode) {
          city = city || fallback.city;
          state = state || fallback.state;
          pincode = fallback.pincode;
        }
      }

      setFormData((p) => ({
        ...p,
        address: formatted,
        city: city || p.city,
        state: state || p.state,
        pincode: pincode || p.pincode,
        latitude: lat,
        longitude: lng,
      }));

      if (inputRef.current) inputRef.current.value = formatted;
      setErrors((prev) => ({ ...prev, address: "" }));
    } catch (err) {
      console.error("onPlaceChanged error:", err);
    }
  };

  const geocodeAddress = async () => {
    try {
      if (!window.google?.maps?.Geocoder) return false;
      if (!formData.address?.trim()) return false;

      const geocoder = new window.google.maps.Geocoder();
      const result = await new Promise((resolve, reject) => {
        geocoder.geocode({ address: formData.address }, (results, status) => {
          if (status === "OK" && results?.[0]) resolve(results[0]);
          else reject(new Error(status || "Geocoding failed"));
        });
      });

     

      const { city, state, pincode } = extractFromPlace(result);

      setFormData((p) => ({
        ...p,
        city: city || p.city,
        state: state || p.state,
        pincode: pincode || p.pincode,
        latitude: result.geometry.location.lat(),
        longitude: result.geometry.location.lng(),
      }));

      return true;
    } catch (err) {
      console.error("geocodeAddress error:", err);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const eObj = validate();
    if (Object.keys(eObj).length) {
      setErrors(eObj);
      return;
    }

    setLoading(true);

    try {
      // if user typed manually & lat/lng missing, geocode it
      if ((!formData.latitude || !formData.longitude) && window.google) {
        const ok = await geocodeAddress();
        if (!ok) {
          alert("Please select address from suggestions.");
          setLoading(false);
          return;
        }
      }

      const res = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/address`,
        {
          patientId,
          ...formData,
          isDefault: false,
          latitude: formData.latitude ? Number(formData.latitude) : null,
          longitude: formData.longitude ? Number(formData.longitude) : null,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          withCredentials: true,
        }
      );

     

      const data = res.data;


      if (res.status == 201) {
          const saved = data.address || data.data || data;
        onSave?.({
        ...saved,
        id: saved?.id ?? saved?.addressId ?? saved?._id,
      });
         // ✅ close modal properly
      onClose?.();
        return;
      }
    
    

   
    } catch (err) {
      console.error("save address error:", err);
      alert(err?.response?.data?.message || "Failed to save address");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={loading ? undefined : onClose}
        />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Add New Address
              </h3>
              <button
                type="button"
                onClick={loading ? undefined : onClose}
                className="text-gray-400 hover:text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Save As */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Save As
                </label>
                <select
                  value={formData.saveas}
                  onChange={(e) => setField("saveas", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm">
                  <option value="Home">Home</option>
                  <option value="Work">Work</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Address */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Address <span className="text-red-500">*</span>
                  <span className="ml-2 text-xs text-gray-500">
                    (Search & select)
                  </span>
                </label>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPin className="h-4 w-4 text-gray-400" />
                  </div>

                  <Autocomplete
                    onLoad={onLoadAuto}
                    onPlaceChanged={onPlaceChanged}
                    options={{
                      // ✅ makes suggestions closer to real addresses
                      types: ["geocode"],
                      fields: [
                        "address_components",
                        "formatted_address",
                        "geometry",
                      ],
                      // optional: restrict to India
                      componentRestrictions: { country: "in" },
                    }}>
                    <input
                      ref={inputRef}
                      value={formData.address}
                      onChange={(e) => {
                        setField("address", e.target.value);
                        setFormData((p) => ({
                          ...p,
                          latitude: null,
                          longitude: null,
                        }));
                      }}
                      placeholder="Search full address"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded text-sm"
                    />
                  </Autocomplete>
                </div>

                {errors.address && (
                  <p className="text-red-500 text-xs mt-1">{errors.address}</p>
                )}
              </div>

              {/* Landmark */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Landmark
                </label>
                <input
                  value={formData.landmark}
                  onChange={(e) => setField("landmark", e.target.value)}
                  placeholder="Nearby landmark (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>

              {/* City / State / Pincode */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={formData.city}
                    onChange={(e) => setField("city", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                  {errors.city && (
                    <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    State <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={formData.state}
                    onChange={(e) => setField("state", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                  {errors.state && (
                    <p className="text-red-500 text-xs mt-1">{errors.state}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Pincode <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={formData.pincode}
                    onChange={(e) =>
                      setField(
                        "pincode",
                        e.target.value.replace(/\D/g, "").slice(0, 6)
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                  {errors.pincode && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.pincode}
                    </p>
                  )}
                </div>
              </div>

              {/* Lat/Lng Preview */}
              {formData.latitude && formData.longitude && (
                <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="font-medium text-blue-800">Lat:</span>
                      <span className="ml-2 text-blue-700">
                        {Number(formData.latitude).toFixed(6)}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-blue-800">Lng:</span>
                      <span className="ml-2 text-blue-700">
                        {Number(formData.longitude).toFixed(6)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="pt-2 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={loading ? undefined : onClose}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50">
                  Close
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? "Saving..." : "Save Address"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewAddressModal;
